import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/shared/components/ui/card";
import { Button } from "@/shared/components/ui/button";
import { LoadingSpinner } from "@/shared/components/ui/loading-spinner";
import { Check, AlertTriangle, XCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import InviteService from "../services/InviteService";

type UnsubscribeState = "loading" | "success" | "notFound" | "error";

export default function Unsubscribe() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [state, setState] = useState<UnsubscribeState>("loading");

  useEffect(() => {
    const processUnsubscribe = async () => {
      // Extract token from URL
      const token = searchParams.get("token");

      // If no token, show error immediately
      if (!token) {
        setState("error");
        return;
      }

      try {
        // Call the unsubscribe API
        const response = await InviteService.unsubscribe(token);

        // Handle response based on status code
        // 200: Removido com sucesso agora
        if (response.status === 200) {
          setState("success");
        } else if (response.status === 404) {
          setState("notFound");
        } else {
          setState("error");
        }
      } catch (error) {
        setState("error");
      }
    };

    processUnsubscribe();
  }, [searchParams]);

  // Loading state
  if (state === "loading") {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4 relative">
        <div className="w-full max-w-md space-y-8 animate-fade-in">
          <div className="flex justify-center">
            <img 
              src="/assets/logo.png" 
              alt="Prodgy Logo" 
              className="h-32 w-auto mb-2"
            />
          </div>
          <Card className="w-full shadow-lg">
            <CardHeader className="space-y-3 pb-4">
              <CardTitle className="text-2xl font-medium tracking-tight text-center">
                {t("auth.unsubscribe.loading.title")}
              </CardTitle>
              <CardDescription className="text-center text-muted-foreground">
                {t("auth.unsubscribe.loading.description")}
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col items-center justify-center text-center py-6">
              <LoadingSpinner size="lg" />
            </CardContent>
          </Card>
        </div>
        
        <div className="absolute bottom-4 right-4">
          <span className="text-sm text-muted-foreground opacity-80">
            {t("auth.login.poweredBy")} <a 
              href="https://programmers.com.br" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="hover:text-muted-foreground/80 transition-colors"
            >
              Programmers
            </a>®
          </span>
        </div>
      </div>
    );
  }

  // Success state
  if (state === "success") {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4 relative">
        <div className="w-full max-w-md space-y-8 animate-fade-in">
          <div className="flex justify-center">
            <img 
              src="/assets/logo.png" 
              alt="Prodgy Logo" 
              className="h-32 w-auto mb-2"
            />
          </div>
          <Card className="w-full shadow-lg">
            <CardHeader className="space-y-3 pb-4">
              <CardTitle className="text-2xl font-medium tracking-tight text-center">
                {t("auth.unsubscribe.success.title")}
              </CardTitle>
              <CardDescription className="text-center text-muted-foreground">
                {t("auth.unsubscribe.success.description")}
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col items-center justify-center text-center py-6">
              <div className="mx-auto mb-6 relative">
                <div className="h-20 w-20 rounded-full bg-green-100 flex items-center justify-center">
                  <div className="h-16 w-16 rounded-full bg-green-200 flex items-center justify-center">
                    <div className="h-12 w-12 rounded-full bg-green-500 flex items-center justify-center animate-pulse">
                      <Check className="h-8 w-8 text-white" strokeWidth={3} />
                    </div>
                  </div>
                </div>
              </div>
              <p className="text-sm text-muted-foreground mb-6">
                {t("auth.unsubscribe.success.message")}
              </p>
              <Button 
                onClick={() => navigate('/login')}
                className="w-full"
                size="lg"
              >
                {t("auth.unsubscribe.success.button")}
              </Button>
            </CardContent>
          </Card>
        </div>
        
        <div className="absolute bottom-4 right-4">
          <span className="text-sm text-muted-foreground opacity-80">
            {t("auth.login.poweredBy")} <a 
              href="https://programmers.com.br" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="hover:text-muted-foreground/80 transition-colors"
            >
              Programmers
            </a>®
          </span>
        </div>
      </div>
    );
  }

  // Not found state
  if (state === "notFound") {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4 relative">
        <div className="w-full max-w-md space-y-8 animate-fade-in">
          <div className="flex justify-center">
            <img 
              src="/assets/logo.png" 
              alt="Prodgy Logo" 
              className="h-32 w-auto mb-2"
            />
          </div>
          <Card className="w-full shadow-lg">
            <CardHeader className="space-y-3 pb-4">
              <CardTitle className="text-2xl font-medium tracking-tight text-center">
                {t("auth.unsubscribe.notFound.title")}
              </CardTitle>
              <CardDescription className="text-center text-muted-foreground">
                {t("auth.unsubscribe.notFound.description")}
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col items-center justify-center text-center py-6">
              <div className="mx-auto mb-6 relative">
                <div className="h-20 w-20 rounded-full bg-yellow-100 flex items-center justify-center">
                  <div className="h-16 w-16 rounded-full bg-yellow-200 flex items-center justify-center">
                    <div className="h-12 w-12 rounded-full bg-yellow-500 flex items-center justify-center">
                      <AlertTriangle className="h-8 w-8 text-white" strokeWidth={3} />
                    </div>
                  </div>
                </div>
              </div>
              <p className="text-sm text-muted-foreground mb-6">
                {t("auth.unsubscribe.notFound.message")}
              </p>
            </CardContent>
          </Card>
        </div>
        
        <div className="absolute bottom-4 right-4">
          <span className="text-sm text-muted-foreground opacity-80">
            {t("auth.login.poweredBy")} <a 
              href="https://programmers.com.br" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="hover:text-muted-foreground/80 transition-colors"
            >
              Programmers
            </a>®
          </span>
        </div>
      </div>
    );
  }

  // Error state
  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4 relative">
      <div className="w-full max-w-md space-y-8 animate-fade-in">
        <div className="flex justify-center">
          <img 
            src="/assets/logo.png" 
            alt="Prodgy Logo" 
            className="h-32 w-auto mb-2"
          />
        </div>
        <Card className="w-full shadow-lg">
          <CardHeader className="space-y-3 pb-4">
            <CardTitle className="text-2xl font-medium tracking-tight text-center">
              {t("auth.unsubscribe.error.title")}
            </CardTitle>
            <CardDescription className="text-center text-muted-foreground">
              {t("auth.unsubscribe.error.description")}
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col items-center justify-center text-center py-6">
            <div className="mx-auto mb-6 relative">
              <div className="h-20 w-20 rounded-full bg-red-100 flex items-center justify-center">
                <div className="h-16 w-16 rounded-full bg-red-200 flex items-center justify-center">
                  <div className="h-12 w-12 rounded-full bg-red-500 flex items-center justify-center">
                    <XCircle className="h-8 w-8 text-white" strokeWidth={3} />
                  </div>
                </div>
              </div>
            </div>
            <p className="text-sm text-muted-foreground mb-6">
              {t("auth.unsubscribe.error.message")}
            </p>
          </CardContent>
        </Card>
      </div>
      
      <div className="absolute bottom-4 right-4">
        <span className="text-sm text-muted-foreground opacity-80">
          {t("auth.login.poweredBy")} <a 
            href="https://programmers.com.br" 
            target="_blank" 
            rel="noopener noreferrer" 
            className="hover:text-muted-foreground/80 transition-colors"
          >
            Programmers
          </a>®
        </span>
      </div>
    </div>
  );
}
